import React, { Fragment, useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import ShopButton from "./ShopButton";
import { useFirestore } from "../firebase/FirestoreContext";
import { collection, getDocs, doc, setDoc, getDoc } from "firebase/firestore";
import { EnvContext } from "../context/EnvContext";
import config from "../config";
import { getAuth } from "firebase/auth";

const ReviewStaff = ({ product }) => {
  const environment = useContext(EnvContext);
  const [reviews, setReviews] = useState(null);
  const db = useFirestore();
  const navigate = useNavigate();
  const collectionPath = environment === "staging" ? config.environment.staging.collectionPath : config.environment.production.collectionPath;
  const auth = getAuth();
  const user = auth.currentUser;
  const [userVotes, setUserVotes] = useState({});

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const reviewsCollection = collection(db, `${collectionPath}Products/ProductsList/DataBase/${product?.id}/ReviewsStaff`);
        const reviewsSnapshot = await getDocs(reviewsCollection);

        const reviewsData = reviewsSnapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .filter((review) => !review.remove_review); // exclude removed reviews

        setReviews(reviewsData);
      } catch (error) {
        console.error("Error fetching reviews:", error);
        setReviews([]);
      }
    };

    const fetchUserVotes = async () => {
      if (!user) return;

      try {
        const userVotesDocRef = doc(db, `${collectionPath}Products/ProductsList/DataBase/${product?.id}/ReviewsStaffVotes`, user.uid);
        const userVotesDoc = await getDoc(userVotesDocRef);

        if (userVotesDoc.exists()) {
          setUserVotes(userVotesDoc.data());
        }
      } catch (error) {
        console.error("Error fetching user votes:", error);
      }
    };

    fetchReviews();
    fetchUserVotes();
  }, [db, product?.id, collectionPath, user]);

  const handleVote = async (reviewId, voteValue) => {
    if (!user) {
      alert("You must be logged in to vote.");
      return;
    }

    try {
      const userVotesDocRef = doc(db, `${collectionPath}Products/ProductsList/DataBase/${product?.id}/ReviewsStaffVotes`, user.uid);
      await setDoc(userVotesDocRef, { [reviewId]: voteValue }, { merge: true });

      // Update local state
      setUserVotes((prevVotes) => ({ ...prevVotes, [reviewId]: voteValue }));
    } catch (error) {
      console.error("Error updating vote:", error);
    }
  };

  if (!reviews) {
    return <div>Loading reviews...</div>;
  }

  if (reviews.length === 0) {
    return;
  }

  return (
    <>
      <div style={styles.reviewList}>
        <div style={styles.header}>
          <div style={styles.title}>Staff Product Review</div>
        </div>
        <div style={{ height: 15 }} />
        {reviews.map((review) => (
          <Fragment key={review.id}>
            <div style={{ display: "flex", paddingBottom: 4, gap: 10, alignItems: "center", marginBottom: 10 }}>
              {review.avatarImageUrl ? (
                <img src={review.avatarImageUrl} alt="avatar" style={{ width: 30, height: 30, borderRadius: "50%", marginRight: 0 }} />
              ) : (
                <div style={{ width: 30, height: 30, borderRadius: "50%", backgroundColor: "#ccc" }} />
              )}
              <div style={{ fontWeight: 600 }}>{review.reviewerName}</div>
            </div>
            <div style={{ marginBottom: 10, fontSize: "1.1rem", fontWeight: "500" }}>{review.reviewTitle}</div>

            {/* images scrollable horizontally */}
            <div style={{ display: "flex", overflowX: "auto", paddingBottom: 10 }}>
              {review.reviewImageUrls?.map((image) => (
                <img key={image} src={image} alt="review" style={{ width: 200, height: 200, borderRadius: 8, marginRight: 10, objectFit: "cover" }} />
              ))}
            </div>
            <div style={{ display: "flex", alignItems: "center", margin: "15px 0" }}>
              {review.reviewText && <div style={{ fontSize: 14, lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: review.reviewText }} />}
            </div>
            <div>
              <div style={{ display: "flex", justifyContent: "center", fontWeight: 700, marginBottom: 10 }}>Did you find this review helpful?</div>
              <div style={{ display: "flex", alignItems: "center", marginTop: 8, gap: 20, justifyContent: "center" }}>
                <div
                  style={{
                    ...styles.reviewThumb,
                    ...(userVotes[review.id] === 1 ? styles.votedThumb : {}),
                    cursor: "pointer",
                  }}
                  onClick={() => handleVote(review.id, 1)}
                >
                  <img
                    src="/images/svg/thumb_up.svg"
                    alt="thumbs up"
                    style={{ width: 20, height: 20 }}
                  // support aspect ration
                  />
                </div>
                <div
                  style={{
                    ...styles.reviewThumb,
                    ...(userVotes[review.id] === -1 ? styles.votedThumb : {}),
                    cursor: "pointer",
                  }}
                  onClick={() => handleVote(review.id, -1)}
                >
                  <img src="/images/svg/thumb_down.svg" alt="thumbs up" style={{ width: 20, height: 20 }} />
                </div>
              </div>
            </div>
          </Fragment>
        ))}
        {reviews?.length > 1 && (
          <ShopButton
            text={"See More Reviews"}
            outlined
            containerStyles={{ width: "100%" }}
            buttonStyle={{ flex: 1 }}
            arrow={true}
            onClick={() => navigate(`/reviews/${product?.id}`)}
          />
        )}
      </div>
      <div css={{ height: 20 }} />
      <div css={{ borderTop: ".5px solid #B3B3B3" }} />
    </>
  );
};

const styles = {
  reviewList: {
    padding: "20px 0",
  },
  title: {
    fontSize: "1.3rem",
    fontWeight: "800",
    display: "flex",
    color: "black",
    paddingBottom: 10,
  },
  header: {
    title: {
      fontSize: "1.15rem",
      fontWeight: "500",
      paddingBottom: 10,
    },
  },
  reviewThumb: {
    height: 42,
    width: 42,
    border: "2px solid black",
    borderRadius: 50,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 8,
  },
  votedThumb: {
    backgroundColor: "rgba(0,120,0,.2)",
  },
};

export default ReviewStaff;
